/**
 * <LoginCodeStep> — la saisie du code reçu par SMS ou par e-mail (organism)
 *
 * Trois règles se lisent ici, et aucune n'est décidée ici :
 * - la **neutralité** : la phrase dit « Si un compte correspond… », qu'un compte existe ou non ;
 * - le **renvoi** : grisé tant que `resendIn` n'est pas retombé à zéro, avec son compte à rebours ;
 * - les **essais** : un code faux vide le champ et dit ce qu'il reste ; un code mort (`dead`) grise
 *   le champ, et le bouton principal devient « Recevoir un nouveau code ».
 *
 * Contrôlé de bout en bout : le compte à rebours lui-même est tenu par le view-model, qui passe
 * les secondes restantes.
 */
import React from "react"
import { View } from "react-native"

import { Button } from "../../atoms/Buttons/Button"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { CodeInput } from "../../atoms/CodeInput"
import { Stack } from "../../atoms/containers/Stack"
import { ErrorText } from "../../atoms/typography/ErrorText"
import { Text } from "../../atoms/typography/Text"
import { AuthHeading } from "../../molecules/AuthHeading"
import { type UILoginCodeChannel } from "../../../ports/primary/ui/types"

/** La longueur du code de connexion. */
const LOGIN_CODE_LENGTH = 6

export type LoginCodeStepProps = {
    /** Par où le code est parti — change la phrase et le geste de retour. */
    channel: UILoginCodeChannel
    /** Le numéro ou l'adresse, tel que la personne l'a saisi. */
    identifier: string
    code: string
    onCodeChange: (code: string) => void
    /** Le refus du serveur : « Code incorrect. Il vous reste 4 essais. » */
    error?: string
    /** Le code ne sert plus (expiré, ou plus d'essai) : champ grisé, il en faut un nouveau. */
    dead?: boolean
    /** L'e-mail porte aussi le bouton « Me connecter » — on le rappelle sous le champ. */
    emailHasLink?: boolean
    /** La vérification part — « Me connecter » tourne. */
    verifying?: boolean
    onSubmit: () => void
    /** Secondes avant de pouvoir redemander un code — 0 : le renvoi est offert. */
    resendIn: number
    /** Un nouveau code part. */
    resending?: boolean
    onResend: () => void
    /** « Nouveau code envoyé. » — après un renvoi, jusqu'à la prochaine erreur. */
    resent?: boolean
    /** Le refus du serveur au renvoi (trop de codes demandés…). */
    resendError?: string
    /** « Changer de numéro » / « Changer d'adresse e-mail ». */
    onChangeIdentifier: () => void
    /** « Utiliser mon mot de passe » — un code par e-mail seulement. */
    onUsePassword?: () => void
    /** « Choisir un nouveau mot de passe » — quand on arrive de « Mot de passe oublié ? ». */
    onChooseNewPassword?: () => void
    testID?: string
}

export function LoginCodeStep(props: LoginCodeStepProps) {
    const {
        channel,
        identifier,
        code,
        onCodeChange,
        error,
        dead = false,
        emailHasLink = false,
        verifying = false,
        onSubmit,
        resendIn,
        resending = false,
        onResend,
        resent = false,
        resendError,
        onChangeIdentifier,
        onUsePassword,
        onChooseNewPassword,
        testID,
    } = props
    const waiting = resendIn > 0
    // Une seule porte pour « Me connecter » et pour la touche d'envoi du clavier.
    const canSubmit = code.length === LOGIN_CODE_LENGTH && !dead && !verifying
    const submit = () => {
        if (canSubmit) onSubmit()
    }

    return (
        <Stack gap="6" testID={testID}>
            <Stack gap="2" align="center">
                <AuthHeading title="Votre code de connexion">
                    Si un compte correspond, un code à {LOGIN_CODE_LENGTH} chiffres vient d'être envoyé{" "}
                    {channel === "sms" ? "par SMS au" : "à"}{" "}
                    <Text weight="bold" color="text-primary">
                        {identifier}
                    </Text>
                    .
                </AuthHeading>
                <TextButton
                    label={channel === "sms" ? "Changer de numéro" : "Changer d'adresse e-mail"}
                    onPress={onChangeIdentifier}
                />
            </Stack>

            <Stack gap="2">
                <CodeInput
                    value={code}
                    onChangeText={onCodeChange}
                    length={LOGIN_CODE_LENGTH}
                    autoFocus
                    disabled={dead}
                    error={error}
                    onSubmitEditing={submit}
                />
                {emailHasLink && error == null && (
                    <Text size="text-sm" color="text-muted" center>
                        Ou touchez « Me connecter » dans l'email.
                    </Text>
                )}
            </Stack>

            <Stack gap="2" align="center">
                {dead ? (
                    <Button
                        fullWidth
                        label={waiting ? `Nouveau code dans ${resendIn} s` : "Recevoir un nouveau code"}
                        disabled={waiting}
                        loading={resending}
                        onPress={onResend}
                    />
                ) : (
                    <>
                        <Button
                            fullWidth
                            label="Me connecter"
                            disabled={!canSubmit}
                            loading={verifying}
                            onPress={submit}
                        />
                        <TextButton
                            label={waiting ? `Renvoyer le code dans ${resendIn} s` : "Renvoyer le code"}
                            disabled={waiting || resending}
                            onPress={onResend}
                        />
                    </>
                )}
                {resent && error == null && (
                    <View accessibilityLiveRegion="polite">
                        <Text size="text-sm" color="success-strong" center>
                            Nouveau code envoyé.
                        </Text>
                    </View>
                )}
                {resendError != null && (
                    <ErrorText center live>
                        {resendError}
                    </ErrorText>
                )}
            </Stack>

            {(onUsePassword != null || onChooseNewPassword != null) && (
                <Stack gap="2" align="center">
                    {onUsePassword != null && <TextButton label="Utiliser mon mot de passe" onPress={onUsePassword} />}
                    {onChooseNewPassword != null && (
                        <TextButton label="Choisir un nouveau mot de passe" onPress={onChooseNewPassword} />
                    )}
                </Stack>
            )}
        </Stack>
    )
}
